import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { showError, showInfo } from "../utils/toast";
import "../../src/sass/components/modalPlanosDisponiveis.scss";

const normalizarTexto = (valor) => String(valor ?? "").trim();

const getPlanoId = (plano) => plano?.ID ?? plano?.Id ?? plano?.id;
const getPlanoCodigo = (plano) => plano?.Codigo ?? plano?.codigo ?? "";
const getPlanoDescricao = (plano) =>
  plano?.Descricao ?? plano?.descricao ?? plano?.Nome ?? plano?.nome ?? "";
const getPlanoValor = (plano) => plano?.Valor ?? plano?.valor ?? "";

const normalizarValorInput = (valor) => {
  let texto = String(valor ?? "");

  texto = texto.replace(/[^\d,\.]/g, "");

  const temVirgula = texto.includes(",");
  const temPonto = texto.includes(".");

  if (temVirgula && temPonto) {
    texto = texto.replace(/\./g, "");
  }

  texto = texto.replace(".", ",");

  const partes = texto.split(",");

  if (partes.length > 1) {
    texto = `${partes[0]},${partes.slice(1).join("").slice(0, 2)}`;
  }

  return texto;
};

const formatarValor = (valor) => {
  const texto = normalizarTexto(valor);

  if (!texto) return "";

  const numero = Number(texto.replace(/\./g, "").replace(",", "."));

  if (Number.isNaN(numero)) return texto;

  return numero.toFixed(2).replace(".", ",");
};

const valorParaApi = (valor) =>
  String(valor ?? "")
    .trim()
    .replace(/\./g, "")
    .replace(",", ".");

const valorEhValido = (valor) => {
  const numero = Number(valorParaApi(valor));
  return !Number.isNaN(numero) && numero > 0;
};

const ModalAdicionarPlanos = ({ clienteId, onClose, onSaveSuccess }) => {
  const [planosDisponiveis, setPlanosDisponiveis] = useState([]);
  const [planosSelecionados, setPlanosSelecionados] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const carregarPlanos = async () => {
    try {
      setLoading(true);

      const { data } = await api.get("/Planos-cadastrados");

      setPlanosDisponiveis(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erro ao buscar serviços disponíveis:", err);
      showError("Erro ao buscar serviços adicionais disponíveis.");
      setPlanosDisponiveis([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarPlanos();
  }, []);

  const planosFiltrados = useMemo(() => {
    const termo = normalizarTexto(search).toLowerCase();

    if (!termo) return planosDisponiveis;

    return planosDisponiveis.filter((plano) => {
      const codigo = String(getPlanoCodigo(plano)).toLowerCase();
      const descricao = String(getPlanoDescricao(plano)).toLowerCase();

      return codigo.includes(termo) || descricao.includes(termo);
    });
  }, [planosDisponiveis, search]);

  const totalSelecionados = useMemo(
    () =>
      Object.values(planosSelecionados).filter((plano) => plano?.selecionado)
        .length,
    [planosSelecionados],
  );

  const handleSelecionar = (planoId, plano) => {
    if (!planoId) return;

    setPlanosSelecionados((prev) => {
      const selecionadoAtual = !!prev[planoId]?.selecionado;
      const valorAtual = prev[planoId]?.valor;
      const valorPadrao = getPlanoValor(plano);

      return {
        ...prev,
        [planoId]: {
          id: planoId,
          idServico: planoId,
          idPlano: planoId,
          codigo: getPlanoCodigo(plano),
          descricao: getPlanoDescricao(plano),
          valor: valorAtual ?? valorPadrao ?? "",
          selecionado: !selecionadoAtual,
        },
      };
    });
  };

  const handleValorChange = (planoId, valor) => {
    setPlanosSelecionados((prev) => ({
      ...prev,
      [planoId]: {
        ...prev[planoId],
        valor: normalizarValorInput(valor),
      },
    }));
  };

  const handleValorBlur = (planoId) => {
    setPlanosSelecionados((prev) => ({
      ...prev,
      [planoId]: {
        ...prev[planoId],
        valor: formatarValor(prev[planoId]?.valor),
      },
    }));
  };

  const handleSalvar = async () => {
    const codigoCliente = normalizarTexto(clienteId);

    if (!codigoCliente) {
      showError("ID do cliente não informado.");
      return;
    }

    const planosParaSalvar = Object.values(planosSelecionados).filter(
      (plano) => plano?.selecionado && valorEhValido(plano?.valor),
    );

    if (planosParaSalvar.length === 0) {
      showInfo("Selecione ao menos um serviço com valor válido.");
      return;
    }

    const payload = {
      planos: planosParaSalvar.map((plano) => {
        const valorApi = valorParaApi(plano.valor);

        return {
          idServico: Number(plano.idServico || plano.id || plano.idPlano),
          idPlano: Number(plano.idPlano || plano.id || plano.idServico),
          ID: Number(plano.id || plano.idServico || plano.idPlano),
          codigo: plano.codigo,
          Codigo: plano.codigo,
          plano: plano.descricao,
          Plano: plano.descricao,
          descricao: plano.descricao,
          Descricao: plano.descricao,
          valorCobrado: valorApi,
          ValorCobrado: valorApi,
        };
      }),
    };

    console.log("Payload ClientesPlanos:", payload);

    try {
      setSaving(true);

      await api.post(`/ClientesPlanos/${codigoCliente}/planos`, payload);

      showInfo("Serviços adicionados ao cliente com sucesso.");

      if (typeof onSaveSuccess === "function") {
        await onSaveSuccess();
      }

      onClose();
    } catch (err) {
      console.error("Erro ao salvar serviços do cliente:", err?.response || err);

      showError(
        err?.response?.data?.detalhes ||
          err?.response?.data?.error ||
          err?.response?.data?.erro ||
          err?.response?.data?.message ||
          "Erro ao salvar os serviços do cliente.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-planos-overlay" onClick={onClose}>
      <div
        className="modal-planos"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-planos-title"
      >
        <header className="modal-planos__header">
          <div>
            <span className="modal-planos__eyebrow">Serviços adicionais</span>
            <h2 id="modal-planos-title">Adicionar ao cliente</h2>
            <p>
              Selecione os serviços cadastrados e confirme o valor cobrado para
              esta licença.
            </p>
          </div>

          <button
            type="button"
            className="modal-planos__close"
            onClick={onClose}
            aria-label="Fechar"
            disabled={saving}
          >
            ×
          </button>
        </header>

        <section className="modal-planos__toolbar">
          <Input
            type="text"
            placeholder="Buscar por código ou descrição..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            disabled={loading || saving}
          />

          <div className="modal-planos__toolbar-info">
            <strong>{planosFiltrados.length}</strong>
            <span>
              {planosFiltrados.length === 1 ? "disponível" : "disponíveis"}
            </span>
          </div>
        </section>

        <section className="lista-planos">
          {loading && (
            <div className="lista-planos__empty">Carregando serviços...</div>
          )}

          {!loading && planosFiltrados.length === 0 && (
            <div className="lista-planos__empty">
              Nenhum serviço adicional encontrado.
            </div>
          )}

          {!loading &&
            planosFiltrados.map((plano) => {
              const planoId = getPlanoId(plano);
              const codigo = getPlanoCodigo(plano);
              const descricao = getPlanoDescricao(plano);
              const valorPadrao = getPlanoValor(plano);
              const selecionado = !!planosSelecionados[planoId]?.selecionado;
              const valorSelecionado = planosSelecionados[planoId]?.valor || "";

              return (
                <article
                  key={planoId}
                  className={`plano-item ${selecionado ? "selected" : ""}`}
                >
                  <label className="plano-check" aria-label="Selecionar serviço">
                    <input
                      type="checkbox"
                      checked={selecionado}
                      onChange={() => handleSelecionar(planoId, plano)}
                      disabled={saving}
                    />
                    <span />
                  </label>

                  <button
                    type="button"
                    className="plano-info"
                    onClick={() => handleSelecionar(planoId, plano)}
                    disabled={saving}
                  >
                    <div className="plano-info__top">
                      <strong>{descricao || "Serviço sem descrição"}</strong>
                      {codigo && <em>#{codigo}</em>}
                    </div>

                    <div className="plano-info__bottom">
                      Valor padrão: <b>R$ {valorPadrao || "0,00"}</b>
                    </div>
                  </button>

                  <div className="plano-valor">
                    <label htmlFor={`valor-plano-${planoId}`}>
                      Valor cobrado
                    </label>
                    <Input
                      id={`valor-plano-${planoId}`}
                      type="text"
                      inputMode="decimal"
                      placeholder="0,00"
                      value={valorSelecionado}
                      onChange={(event) =>
                        handleValorChange(planoId, event.target.value)
                      }
                      onBlur={() => handleValorBlur(planoId)}
                      disabled={!selecionado || saving}
                    />
                  </div>
                </article>
              );
            })}
        </section>

        <footer className="modal-planos__footer">
          <div className="modal-planos__summary">
            {totalSelecionados === 1
              ? "1 serviço selecionado"
              : `${totalSelecionados} serviços selecionados`}
          </div>

          <div className="acoes">
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>

            <Button onClick={handleSalvar} disabled={saving || loading}>
              {saving ? "Salvando..." : "Salvar serviços"}
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ModalAdicionarPlanos;